import { useTranslation } from "@/hooks";
import { parseSearchParams } from "@/utils/parse";
import { Bytes } from "@/utils/unit";
import {
  Box,
  Button,
  Flex,
  Menu,
  NumberInput,
  Select,
  Text,
} from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import { IconChevronDown } from "@tabler/icons-react";
import type { DiscoverFilterValues } from "@/types/features/discover";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";

export function DiscoverFilterSize({
  form,
  onSubmit,
}: {
  form: UseFormReturnType<DiscoverFilterValues>;
  onSubmit: (values: DiscoverFilterValues) => void;
}) {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);
  const [searchParams] = useSearchParams();
  const sizeValue = form.values.size || {};
  const [minUnit, setMinUnit] = useState<string>("MB");
  const [maxUnit, setMaxUnit] = useState<string>("MB");

  // Read filter values directly from search params for labels
  const queryValues = useMemo(
    () => parseSearchParams(searchParams),
    [searchParams]
  );
  const querySize = queryValues.size || {};

  // Also read raw byte values from search params to handle byte conversion correctly
  const rawByteValues = useMemo(() => {
    const minBytes = searchParams.get("byte_count__gte");
    const maxBytes = searchParams.get("byte_count__lte");
    return {
      minBytes: minBytes ? parseFloat(minBytes) : undefined,
      maxBytes: maxBytes ? parseFloat(maxBytes) : undefined,
    };
  }, [searchParams]);

  const initialSizeRef = useRef<typeof sizeValue>(
    form.values.size ? { ...form.values.size } : {}
  );
  const prevOpenedRef = useRef(opened);
  const unitsInitializedRef = useRef(false);

  useEffect(() => {
    // Only update initial values when menu transitions from closed to open
    if (opened && !prevOpenedRef.current) {
      // If form values are empty, initialize from query values
      const currentSize = form.values.size || {};
      if (
        !currentSize.minSize &&
        !currentSize.maxSize &&
        !currentSize.minColumns &&
        !currentSize.maxColumns &&
        !currentSize.minRows &&
        !currentSize.maxRows
      ) {
        if (
          querySize &&
          (querySize.minSize ||
            querySize.maxSize ||
            querySize.minColumns ||
            querySize.maxColumns ||
            querySize.minRows ||
            querySize.maxRows)
        ) {
          // Convert bytes to MB if needed
          // Prefer raw byte values from search params for accurate conversion
          const convertedSize = { ...querySize };
          if (rawByteValues.minBytes !== undefined) {
            convertedSize.minSize = Bytes.megabytes(rawByteValues.minBytes);
          } else if (
            querySize.minSize !== undefined &&
            querySize.minSize > 1000
          ) {
            convertedSize.minSize = Bytes.megabytes(querySize.minSize);
          }
          if (rawByteValues.maxBytes !== undefined) {
            convertedSize.maxSize = Bytes.megabytes(rawByteValues.maxBytes);
          } else if (
            querySize.maxSize !== undefined &&
            querySize.maxSize > 1000
          ) {
            convertedSize.maxSize = Bytes.megabytes(querySize.maxSize);
          }
          form.setFieldValue("size", convertedSize);
        }
      }
      initialSizeRef.current = form.values.size ? { ...form.values.size } : {};
    }
    prevOpenedRef.current = opened;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, querySize, rawByteValues]);

  const isFormDirty = useMemo(() => {
    const initial = initialSizeRef.current || {};
    const current = form.values.size || {};

    // Compare all size properties
    return (
      initial.minColumns !== current.minColumns ||
      initial.maxColumns !== current.maxColumns ||
      initial.minRows !== current.minRows ||
      initial.maxRows !== current.maxRows ||
      initial.minSize !== current.minSize ||
      initial.maxSize !== current.maxSize
    );
  }, [form.values.size]);

  const handleApply = () => {
    onSubmit(form.values);
    initialSizeRef.current = form.values.size ? { ...form.values.size } : {};
    setOpened(false);
  };

  const handleReset = () => {
    form.setFieldValue("size", undefined);
    const emptySize = {};
    initialSizeRef.current = emptySize;
    onSubmit({ ...form.values, size: undefined });
    setOpened(false);
  };

  // Convert value in unit to MB (for storage in form)
  // The form stores values in MB, so we need to convert from user's selected unit to MB
  const unitToMB = (value: number, unit: string): number => {
    switch (unit) {
      case "KB":
        return value / 1024; // KB to MB
      case "MB":
        return value; // Already in MB
      case "GB":
        return value * 1024; // GB to MB
      default:
        return value;
    }
  };

  // Convert MB (from form) to value in unit (for display)
  const mbToUnit = (mb: number, unit: string): number => {
    switch (unit) {
      case "KB":
        return mb * 1024; // MB to KB
      case "MB":
        return mb; // Already in MB
      case "GB":
        return mb / 1024; // MB to GB
      default:
        return mb;
    }
  };

  // Determine appropriate unit for a byte value
  const getAppropriateUnit = (bytes: number): string => {
    const MB_IN_BYTES = 1024 * 1024;
    const GB_IN_BYTES = 1024 * 1024 * 1024;

    if (bytes < MB_IN_BYTES) return "KB";
    if (bytes < GB_IN_BYTES) return "MB";
    return "GB";
  };

  const hasSizeFilter =
    querySize.minColumns !== undefined ||
    querySize.maxColumns !== undefined ||
    querySize.minRows !== undefined ||
    querySize.maxRows !== undefined ||
    querySize.minSize !== undefined ||
    querySize.maxSize !== undefined;

  // Get bytes from raw params, form, or query - always work with bytes
  const minBytes = useMemo(() => {
    if (rawByteValues.minBytes !== undefined) return rawByteValues.minBytes;
    const formSize = form.values.size;
    if (formSize?.minSize !== undefined) return formSize.minSize * 1024 * 1024;
    if (querySize.minSize !== undefined) {
      // If > 1000, it's already in bytes, otherwise it's in MB
      return querySize.minSize > 1000
        ? querySize.minSize
        : querySize.minSize * 1024 * 1024;
    }
    return undefined;
  }, [rawByteValues.minBytes, form.values.size, querySize.minSize]);

  const maxBytes = useMemo(() => {
    if (rawByteValues.maxBytes !== undefined) return rawByteValues.maxBytes;
    const formSize = form.values.size;
    if (formSize?.maxSize !== undefined) return formSize.maxSize * 1024 * 1024;
    if (querySize.maxSize !== undefined) {
      return querySize.maxSize > 1000
        ? querySize.maxSize
        : querySize.maxSize * 1024 * 1024;
    }
    return undefined;
  }, [rawByteValues.maxBytes, form.values.size, querySize.maxSize]);

  // Calculate display values for min and max based on selected units
  // Convert from MB (stored in form) to the selected unit
  const minSizeValue = useMemo(() => {
    const formSize = form.values.size;
    if (formSize?.minSize === undefined) return undefined;
    return mbToUnit(formSize.minSize, minUnit);
  }, [form.values.size?.minSize, minUnit]);

  const maxSizeValue = useMemo(() => {
    const formSize = form.values.size;
    if (formSize?.maxSize === undefined) return undefined;
    return mbToUnit(formSize.maxSize, maxUnit);
  }, [form.values.size?.maxSize, maxUnit]);

  // Initialize units when menu opens based on current byte values
  useEffect(() => {
    if (opened && !unitsInitializedRef.current) {
      if (minBytes !== undefined) {
        setMinUnit(getAppropriateUnit(minBytes));
      }
      if (maxBytes !== undefined) {
        setMaxUnit(getAppropriateUnit(maxBytes));
      }
      unitsInitializedRef.current = true;
    }
    if (!opened) {
      unitsInitializedRef.current = false;
    }
    // Only depend on opened state to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  return (
    <Menu
      position="bottom-start"
      width={300}
      opened={opened}
      onChange={setOpened}
      closeOnClickOutside={true}
      closeOnItemClick={false}
    >
      <Menu.Target>
        <Button
          size="xs"
          variant="subtle"
          rightSection={
            <IconChevronDown
              color={
                hasSizeFilter
                  ? "var(--mantine-color-eu_flag-9)"
                  : "var(--mantine-color-gray-9)"
              }
              size={12}
            />
          }
        >
          <Text
            fz={15}
            c={hasSizeFilter ? "eu_flag.9" : "gray.9"}
            fw={hasSizeFilter ? 600 : 400}
          >
            {t("filters").size}
          </Text>
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Flex direction="column" p="xs" gap="sm">
          <Text fz={14} fw={500}>
            {t("filters").datasetSize}
          </Text>
          {/* Min size row */}
          <Flex gap="sm" align="flex-end">
            <Box style={{ flex: 1 }}>
              <NumberInput
                label="Min Size"
                placeholder="0"
                value={minSizeValue ?? ""}
                onChange={(value) => {
                  if (typeof value === "number") {
                    // Convert from user's unit to MB for storage
                    const mb = unitToMB(value, minUnit);
                    form.setFieldValue("size", {
                      ...sizeValue,
                      minSize: mb,
                    });
                  } else {
                    form.setFieldValue("size", {
                      ...sizeValue,
                      minSize: undefined,
                    });
                  }
                }}
              />
            </Box>
            <Box
              style={{ flex: 1 }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Select
                data={["KB", "MB", "GB"]}
                value={minUnit}
                onChange={(value) => {
                  if (value) {
                    setMinUnit(value);
                  }
                }}
              />
            </Box>
          </Flex>
          {/* Max size row */}
          <Flex gap="sm" align="flex-end">
            <Box style={{ flex: 1 }}>
              <NumberInput
                label="Max Size"
                placeholder="0"
                value={maxSizeValue ?? ""}
                onChange={(value) => {
                  if (typeof value === "number") {
                    // Convert from user's unit to MB for storage
                    const mb = unitToMB(value, maxUnit);
                    form.setFieldValue("size", {
                      ...sizeValue,
                      maxSize: mb,
                    });
                  } else {
                    form.setFieldValue("size", {
                      ...sizeValue,
                      maxSize: undefined,
                    });
                  }
                }}
              />
            </Box>
            <Box
              style={{ flex: 1 }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Select
                data={["KB", "MB", "GB"]}
                value={maxUnit}
                onChange={(value) => {
                  if (value) {
                    setMaxUnit(value);
                  }
                }}
              />
            </Box>
          </Flex>
          <Flex gap="sm" mt="xl">
            <NumberInput
              label={t("filters").minColumns}
              placeholder="0"
              value={sizeValue.minColumns ?? ""}
              onChange={(value) =>
                form.setFieldValue("size", {
                  ...sizeValue,
                  minColumns: typeof value === "number" ? value : undefined,
                })
              }
            />
            <NumberInput
              label={t("filters").maxColumns}
              placeholder="0"
              value={sizeValue.maxColumns ?? ""}
              onChange={(value) =>
                form.setFieldValue("size", {
                  ...sizeValue,
                  maxColumns: typeof value === "number" ? value : undefined,
                })
              }
            />
          </Flex>
          <Flex gap="sm">
            <NumberInput
              label={t("filters").minRows}
              placeholder="0"
              value={sizeValue.minRows ?? ""}
              onChange={(value) =>
                form.setFieldValue("size", {
                  ...sizeValue,
                  minRows: typeof value === "number" ? value : undefined,
                })
              }
            />
            <NumberInput
              label={t("filters").maxRows}
              placeholder="0"
              value={sizeValue.maxRows ?? ""}
              onChange={(value) =>
                form.setFieldValue("size", {
                  ...sizeValue,
                  maxRows: typeof value === "number" ? value : undefined,
                })
              }
            />
          </Flex>
          <Button
            fullWidth
            mt="sm"
            onClick={handleApply}
            disabled={!isFormDirty}
          >
            {t("filters").apply}
          </Button>
          <Button fullWidth variant="subtle" color="gray" onClick={handleReset}>
            {t("filters").reset}
          </Button>
        </Flex>
      </Menu.Dropdown>
    </Menu>
  );
}
